// utils/selfPacedApi.js
// ──────────────────────────────────────────────────────────────────────────────
// Centralised API helper for all self-paced / LXP endpoints.
// Every function returns the axios promise so callers can await / .then().
// ──────────────────────────────────────────────────────────────────────────────

import apiClient from './apiClient';

const SP = '/self-paced';

// ═══════════════════════════════════════════════════════════════════════════════
// PUBLIC CATALOG
// ═══════════════════════════════════════════════════════════════════════════════

export const listPublicCourses = (params = {}) =>
  apiClient.get(`${SP}/public/courses`, { params });

export const getPublicCourse = (idOrSlug) =>
  apiClient.get(`${SP}/public/courses/${idOrSlug}`);

export const verifyCertificate = (certNo) =>
  apiClient.get(`${SP}/certificates/verify/${certNo}`);

// ═══════════════════════════════════════════════════════════════════════════════
// ENROLLMENT (student)
// ═══════════════════════════════════════════════════════════════════════════════

export const purchaseCourse = (courseId) =>
  apiClient.post(`${SP}/enroll/purchase`, { courseId });

export const verifyPurchase = (body) =>
  apiClient.post(`${SP}/enroll/verify`, body);

export const getMyEnrollments = () =>
  apiClient.get(`${SP}/enroll/my`);

export const adminGrantEnrollment = (body) =>
  apiClient.post(`${SP}/enroll/admin-grant`, body);

// ═══════════════════════════════════════════════════════════════════════════════
// THUMBNAIL UPLOAD
// ═══════════════════════════════════════════════════════════════════════════════

export const getCourseThumbnailUploadUrl = (courseId, body) =>
  apiClient.post(`${SP}/courses/${courseId}/thumbnail`, body, body instanceof FormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : undefined);

// ═══════════════════════════════════════════════════════════════════════════════
// MODULE CRUD (admin)
// ═══════════════════════════════════════════════════════════════════════════════

export const createModule = (body) =>
  apiClient.post(`${SP}/modules`, body);

export const updateModule = (id, body) =>
  apiClient.put(`${SP}/modules/${id}`, body);

export const deleteModule = (id) =>
  apiClient.delete(`${SP}/modules/${id}`);

// ═══════════════════════════════════════════════════════════════════════════════
// LESSON CRUD (admin)
// ═══════════════════════════════════════════════════════════════════════════════

export const createLesson = (body) =>
  apiClient.post(`${SP}/lessons`, body, body instanceof FormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : undefined);

export const confirmLessonUpload = (id) =>
  apiClient.post(`${SP}/lessons/${id}/confirm-upload`);

export const updateLesson = (id, body) =>
  apiClient.put(`${SP}/lessons/${id}`, body);

export const deleteLesson = (id) =>
  apiClient.delete(`${SP}/lessons/${id}`);

// ═══════════════════════════════════════════════════════════════════════════════
// CONTENT DELIVERY
// ═══════════════════════════════════════════════════════════════════════════════

export const getCurriculum = (courseId) =>
  apiClient.get(`${SP}/courses/${courseId}/curriculum`);

export const getLessonContent = (lessonId) =>
  apiClient.get(`${SP}/lessons/${lessonId}/content`);

export const streamLessonVideo = (lessonId) =>
  apiClient.get(`${SP}/lessons/${lessonId}/stream`);

// ═══════════════════════════════════════════════════════════════════════════════
// PROGRESS (student)
// ═══════════════════════════════════════════════════════════════════════════════

export const updateLessonProgress = (lessonId, body) =>
  apiClient.patch(`${SP}/lessons/${lessonId}/progress`, body);

export const getCourseProgress = (courseId) =>
  apiClient.get(`${SP}/courses/${courseId}/progress`);

// ═══════════════════════════════════════════════════════════════════════════════
// QUIZ
// ═══════════════════════════════════════════════════════════════════════════════

export const createQuiz = (body) =>
  apiClient.post(`${SP}/quiz`, body);

export const updateQuiz = (id, body) =>
  apiClient.put(`${SP}/quiz/${id}`, body);

export const deleteQuiz = (id) =>
  apiClient.delete(`${SP}/quiz/${id}`);

export const getQuiz = (quizId) =>
  apiClient.get(`${SP}/quiz/${quizId}`);

export const startQuiz = (quizId) =>
  apiClient.post(`${SP}/quiz/${quizId}/start`);

export const submitQuiz = (quizId, body) =>
  apiClient.post(`${SP}/quiz/${quizId}/submit`, body);

// ═══════════════════════════════════════════════════════════════════════════════
// CERTIFICATES
// ═══════════════════════════════════════════════════════════════════════════════

export const getMyCertificates = () =>
  apiClient.get(`${SP}/certificates/my`);

export const getCertificate = (courseId) =>
  apiClient.get(`${SP}/certificates/${courseId}`);

// ═══════════════════════════════════════════════════════════════════════════════
// ADMIN: Enrollment analytics
// ═══════════════════════════════════════════════════════════════════════════════

export const getEnrollmentsByCourse = (courseId, params = {}) =>
  apiClient.get(`${SP}/admin/courses/${courseId}/enrollments`, { params });

// ═══════════════════════════════════════════════════════════════════════════════
// ADMIN: Certificate Template Settings
// ═══════════════════════════════════════════════════════════════════════════════

export const getCertificateSettings = () =>
  apiClient.get(`${SP}/certificate-settings`);

export const updateCertificateSettings = (data) =>
  apiClient.put(`${SP}/certificate-settings`, data);

export const getUploadBackgroundUrl = (body) =>
  apiClient.post(`${SP}/certificate-settings/upload-background`, body, body instanceof FormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : undefined);

// ═══════════════════════════════════════════════════════════════════════════════
// COURSE CRUD — uses the existing /courses endpoints with courseType param
// ═══════════════════════════════════════════════════════════════════════════════

export const getSelfPacedCourses = (params = {}) =>
  apiClient.get('/courses/list', { params: { ...params, courseType: 'self-paced', q: 'info' } });

export const getSelfPacedCourse = (id) =>
  apiClient.get(`/courses/${id}`);

export const addSelfPacedCourse = (body) =>
  apiClient.post('/courses/add', { ...body, courseType: 'self-paced' });

export const updateSelfPacedCourse = (id, body) =>
  apiClient.put(`/courses/${id}`, body);

export const deleteSelfPacedCourse = (id) =>
  apiClient.delete(`/courses/delete/${id}`);

// ═══════════════════════════════════════════════════════════════════════════════
// DOCUMENT AI EXPLANATIONS
// ═══════════════════════════════════════════════════════════════════════════════

const DOC_AI = '/documents';

/** Admin — trigger AI explanation generation for a lesson */
export const triggerAIExplanation = (lessonId) =>
  apiClient.post(`${DOC_AI}/trigger-ai`, { lessonId });

/** Admin — retry failed AI explanation for a lesson */
export const retryAIExplanation = (lessonId) =>
  apiClient.post(`${DOC_AI}/${lessonId}/retry`);

/** Admin/Student — get AI processing status for a lesson */
export const getAIStatus = (lessonId) =>
  apiClient.get(`${DOC_AI}/${lessonId}/status`);

/** Admin — get bulk AI status for multiple lessons */
export const getBulkAIStatus = (lessonIds) =>
  apiClient.post(`${DOC_AI}/bulk-status`, { lessonIds });

/** Student/Admin — get AI explanations for a lesson (paginated) */
export const getAIExplanations = (lessonId, params = {}) =>
  apiClient.get(`${DOC_AI}/${lessonId}/explanations`, { params });

/** Student/Admin — get parsed document pages for a lesson */
export const getDocumentPages = (lessonId) =>
  apiClient.get(`${DOC_AI}/${lessonId}/pages`);

/** Admin — get AI settings for the institute */
export const getAISettings = () =>
  apiClient.get(`${DOC_AI}/ai-settings`);

/** Admin — update AI settings for the institute */
export const updateAISettings = (body) =>
  apiClient.put(`${DOC_AI}/ai-settings`, body);

/** Admin — get AI usage stats for the institute */
export const getAIUsage = () =>
  apiClient.get(`${DOC_AI}/ai-usage`);

/** Admin — get BullMQ queue metrics */
export const getQueueMetrics = () =>
  apiClient.get(`${DOC_AI}/queue-metrics`);
